import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import useQueryParam from '@jwp/ott-ui-react/src/hooks/useQueryParam';
import usePlaylist from '@jwp/ott-hooks-react/src/usePlaylist';
import useMedia from '@jwp/ott-hooks-react/src/useMedia';
import { getReelsPlaylistId } from '@jwp/ott-common/src/utils/reels';
import ArrowLeft from '@jwp/ott-theme/assets/icons/arrow_left.svg?react';
import ArrowUp from '@jwp/ott-theme/assets/icons/Arrow_up.svg?react';
import ArrowDown from '@jwp/ott-theme/assets/icons/Arrow_down.svg?react';
import AudioOn from '@jwp/ott-theme/assets/icons/audio-on.svg?react';
import AudioOff from '@jwp/ott-theme/assets/icons/audio-off.svg?react';
import PlayIcon from '@jwp/ott-theme/assets/icons/play.svg?react';
import DefaultAvatar from '@jwp/ott-theme/assets/profiles/avatar.png';
import { getModule } from '@jwp/ott-common/src/modules/container';

import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';

import ShareButton from '../ShareButton/ShareButton';
import { shareMediaURL } from '@jwp/ott-common/src/utils/urlFormatting';
import { modalURLFromLocation } from '../../utils/location';
import type { JWPlayer } from '../../../types/jwplayer';
import Player from '../../components/Player/Player';
import Icon from '../Icon/Icon';
import IconButton from '../IconButton/IconButton';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import CollapsibleText from '../CollapsibleText/CollapsibleText';
import { handleBackNavigate } from '../../utils/handleBackNavigate';
import { renderToString } from 'react-dom/server';
import type { PlaylistItem, videoDetails } from '@jwp/ott-common/types/playlist';
import ApiService from '@jwp/ott-common/src/services/ApiService';
import env from '@jwp/ott-common/src/env';
import { useMutation } from '@apollo/client/react';
import { LIKE_MEDIAS, UNLIKE_MEDIAS } from '@jwp/ott-common/src/graphql/mutation/reels.mutation';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import LikeIcon from '@jwp/ott-theme/assets/icons/component-icons/LikeIcon';

import styles from './VerticalPlayer.module.scss';
import ProfileCircle from '../ProfileCircle/ProfileCircle';

type Props = {
  playlist?: PlaylistItem[];
  item?: PlaylistItem | videoDetails;
  primaryMetadata?: React.ReactNode;
  /** Pre-loaded media from a shared `/m/{id}` link so playback can start before the shelf playlist loads. */
  initialMedia?: PlaylistItem;
};

const VERTICAL_PLAYER_PLAYLIST_KEY = 'verticalPlayerPlaylist';

type VerticalPlayerPlaylistSession = {
  userId: string | null;
  playlist: PlaylistItem[];
};

const getVerticalPlayerSession = (): VerticalPlayerPlaylistSession | null => {
  const raw = sessionStorage.getItem(VERTICAL_PLAYER_PLAYLIST_KEY);
  if (!raw) { return null; }

  const parsed = JSON.parse(raw);
  if (Array.isArray(parsed)) {
    return { userId: null, playlist: parsed };
  }
  return parsed as VerticalPlayerPlaylistSession;
};

const saveVerticalPlayerSession = (playlist: PlaylistItem[], userId: string | null) => {
  sessionStorage.setItem(VERTICAL_PLAYER_PLAYLIST_KEY, JSON.stringify({ userId, playlist }));
};

const isSessionUserMatch = (sessionUserId: string | null, currentUserId: string | null) =>
  sessionUserId === currentUserId && currentUserId !== null;

const getActiveMediaIdFromPath = (fallback?: string) => {
  const fromPath = window.location.pathname.match(/\/(?:reels|m)\/([^/]+)/)?.[1];
  return fromPath || fallback || '';
};

const matchesReelId = (item: PlaylistItem, id: string) => {
  if (!id) { return false; }
  return item.mediaid === id || item.ma_id === id;
};

const findReelIndex = (items: PlaylistItem[], id: string) => {
  if (!id) { return -1; }
  return items.findIndex((item) => matchesReelId(item, id));
};

const getReelTargetId = (pathId: string | undefined, initialMedia?: PlaylistItem) =>
  getActiveMediaIdFromPath(pathId) || initialMedia?.mediaid || '';

// isLikedByUser is per-user; likeCount is global — API values take precedence on refresh,
// UNLESS the session item is still awaiting server confirmation for an optimistic like/unlike
// (see persistLikeToPlaylist / flushPendingActions), in which case the session value wins so a
// stale/pending API payload can't silently revert the user's own action.
const mergeSessionDataIntoPlaylist = (
  playlistItems: PlaylistItem[],
  session: VerticalPlayerPlaylistSession,
  currentUserId: string | null,
): PlaylistItem[] => {
  const sameUser = isSessionUserMatch(session.userId, currentUserId);
  const sessionMap = new Map(session.playlist.map((item) => [item.ma_id as string, item]));
  return playlistItems.map((item) => {
    const sessionItem = sessionMap.get(item.ma_id as string);
    if (!sessionItem) { return item; }

    // If this item still has an unflushed local like/unlike action, trust the session
    // (it represents the user's own most-recent action, which the API may not reflect yet).
    const hasPendingAction = !!(sessionItem as any).__hasPendingLikeAction;

    if (hasPendingAction && sameUser) {
      return {
        ...item,
        isLikedByUser: !!sessionItem.isLikedByUser,
        likeCount: typeof sessionItem.likeCount === 'number' ? sessionItem.likeCount : item.likeCount,
      };
    }

    return {
      ...item,
      isLikedByUser:
        typeof item.isLikedByUser === 'boolean' ? item.isLikedByUser : sameUser ? !!sessionItem.isLikedByUser : false,
      likeCount: typeof item.likeCount === 'number' ? item.likeCount : sessionItem.likeCount ?? 0,
    };
  });
};

const VerticalPlayer: React.FC<Props> = ({ playlist: propsPlaylist, primaryMetadata, item, initialMedia }) => {
  const { t } = useTranslation('video');
  const navigate = useNavigate();
  const location = useLocation();
  const { id: mediaId } = useParams();
  const sharedTargetIdRef = useRef(getReelTargetId(mediaId, initialMedia));
  const lastMediaIdRef = useRef(sharedTargetIdRef.current || mediaId);
  const queryPlaylistId = useQueryParam('r') || useQueryParam('playlistId');
  const playlistId = queryPlaylistId || getReelsPlaylistId() || undefined;
  const modalView = useQueryParam('u');
  const hasHydratedPlaylistRef = useRef(false);

  const { isShareEnabled } = useAppConfig();

  // const { data: fetchedPlaylist, isLoading } = usePlaylist(playlistId || undefined);

  const statePlaylist = location.state?.playlistItems;

  const userId = useAccountStore(({ user }) => user?.id ?? null);

  // Shared / refreshed entry: don't reuse stale like counts from a previous session
  useEffect(() => {
    if (initialMedia?.mediaid) {
      sessionStorage.removeItem(VERTICAL_PLAYER_PLAYLIST_KEY);
    }
  }, [initialMedia?.mediaid]);

  useEffect(() => {
    const playlistItems = location.state?.playlistItems;
    if (!playlistItems?.length) { return; }

    const currentUserId = useAccountStore.getState().user?.id ?? null;
    const session = getVerticalPlayerSession();
    const sessionUserId = session?.userId ?? null;

    // Don't overwrite a user-scoped session before auth has loaded
    if (!currentUserId && sessionUserId) { return; }

    const playlistToSave = session
      ? mergeSessionDataIntoPlaylist(playlistItems, session, currentUserId)
      : playlistItems;

    saveVerticalPlayerSession(playlistToSave, currentUserId);
  }, [location.state?.playlistItems, userId]);
  const stateItem = location.state?.item;

  const pathTargetId = getReelTargetId(mediaId, initialMedia);
  const sharedMediaId = pathTargetId || undefined;
  const verticalPlayerSession = getVerticalPlayerSession();
  const statePlayListFromSession = verticalPlayerSession?.playlist ?? null;
  const sessionHasTarget =
    !!pathTargetId && statePlayListFromSession?.length
      ? findReelIndex(statePlayListFromSession, pathTargetId) >= 0
      : false;

  // NOTE: intentionally does NOT fall back to sessionStorage here.
  // sessionStorage can be stale (another user may have liked/unliked this video since it was
  // cached), so a page refresh must always be treated as "no navigation playlist" and go through
  // usePlaylist() to fetch fresh data. sessionStorage is still used to seed the *initial* render
  // (see playlist useState below) so there's no blank flash, but it is corrected once the fresh
  // fetch resolves (see the refreshSync effect further down).
  const hasNavigationPlaylist = !initialMedia && Boolean(statePlaylist?.length);

  // True on a hard refresh / direct load where we only have sessionStorage to go on (no router
  // navigation state). Used later to know we must reconcile with a fresh API fetch once it lands.
  const isSessionOnlyEntryRef = useRef(!initialMedia && !statePlaylist?.length && Boolean(statePlayListFromSession?.length));

  const { data: fetchedPlaylist, isLoading, refetch } = usePlaylist(
    hasNavigationPlaylist ? undefined : playlistId || undefined,
    sharedMediaId ? { mediaId: sharedMediaId } : {},
  );

  const [playlist, setPlaylist] = useState<PlaylistItem[]>(() => {
    if (initialMedia) { return [initialMedia]; }

    const currentUserId = useAccountStore.getState().user?.id ?? null;
    const session = getVerticalPlayerSession();
    const sessionPlaylist = session?.playlist ?? null;
    let initialPlaylist = location.state?.playlistItems || [];

    if (session?.playlist?.length && initialPlaylist.length > 0) {
      initialPlaylist = mergeSessionDataIntoPlaylist(initialPlaylist, session, currentUserId);
    }

    if (initialPlaylist.length > 0) { return initialPlaylist; }

    const sessionTargetId = getReelTargetId(mediaId, initialMedia);
    const sessionContainsTarget =
      !!sessionTargetId && sessionPlaylist?.length ? findReelIndex(sessionPlaylist, sessionTargetId) >= 0 : false;

    if (sessionPlaylist?.length && session && (!sessionTargetId || sessionContainsTarget)) {
      // Seed from sessionStorage for a fast first paint. This is treated as provisional —
      // the refreshSync effect below will overwrite likeCount/isLikedByUser with fresh API
      // data as soon as it arrives, since another user may have changed the count meanwhile.
      return mergeSessionDataIntoPlaylist(sessionPlaylist, session, currentUserId);
    }

    return [];
  });
  const playlistRef = useRef(playlist);
  useEffect(() => {
    playlistRef.current = playlist;
  }, [playlist]);

  const PAGE_SIZE = 25;
  const apiService = getModule(ApiService);

  const [offset, setOffset] = useState(PAGE_SIZE);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // const playlist = useMemo(() => propsPlaylist || fetchedPlaylist?.playlist || [], [propsPlaylist, fetchedPlaylist?.playlist]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentIndexRef = useRef(currentIndex);
  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  const activeMediaId = sharedTargetIdRef.current;
  const shouldLoadTargetMedia = !initialMedia && !!activeMediaId && !hasNavigationPlaylist;
  const { data: targetMedia, isLoading: isTargetMediaLoading } = useMedia(activeMediaId, shouldLoadTargetMedia);

  const metadataString = primaryMetadata ? renderToString(primaryMetadata) : '';

  const [playerInstance, setPlayerInstance] = useState<JWPlayer | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [showVolumeBar, setShowVolumeBar] = useState(false);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');
  const [isAnimating, setIsAnimating] = useState(false);

  const isLoggedIn = !!userId;
  const authClient = getAuthApolloClient();
  const [likeMedias] = useMutation(LIKE_MEDIAS, { client: authClient });
  const [unlikeMedias] = useMutation(UNLIKE_MEDIAS, { client: authClient });

  const [likeStates, setLikeStates] = useState<Record<string, { isLiked: boolean; count: number }>>({});
  const pendingActionsRef = useRef<Record<string, { originalIsLiked: boolean; currentIsLiked: boolean }>>({});
  const timerRef = useRef<any>(null);

  const currentVideo = playlist[currentIndex];

  const currentLikeInfo = useMemo(() => {
    if (!currentVideo) { return { isLiked: false, count: 0 }; }
    const maId = (currentVideo.ma_id as string) || currentVideo.mediaid;
    if (likeStates[maId]) {
      return likeStates[maId];
    }
    return {
      isLiked: !!currentVideo.isLikedByUser,
      count: (currentVideo.likeCount as number) || 0,
    };
  }, [currentVideo, likeStates]);

  const persistLikeToPlaylist = useCallback((maId: string, isLiked: boolean, count: number) => {
    const updatedPlaylist = playlistRef.current.map((item) => {
      if ((item.ma_id as string) !== maId) { return item; }
      // mark this item as having an unconfirmed pending action so mergeSessionDataIntoPlaylist
      // knows not to let a stale API payload overwrite it before flushPendingActions confirms it
      return { ...item, isLikedByUser: isLiked, likeCount: count, __hasPendingLikeAction: true } as PlaylistItem;
    });
    playlistRef.current = updatedPlaylist;
    saveVerticalPlayerSession(updatedPlaylist, useAccountStore.getState().user?.id ?? null);
    setPlaylist((prev) =>
      prev.map((item) => {
        if ((item.ma_id as string) !== maId) { return item; }
        return { ...item, isLikedByUser: isLiked, likeCount: count, __hasPendingLikeAction: true } as PlaylistItem;
      }),
    );
  }, []);

  const flushPendingActions = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const actions = pendingActionsRef.current;
    const toLike: string[] = [];
    const toUnlike: string[] = [];

    Object.entries(actions).forEach(([maId, { originalIsLiked, currentIsLiked }]) => {
      if (originalIsLiked !== currentIsLiked) {
        if (currentIsLiked) {
          toLike.push(maId);
        } else {
          toUnlike.push(maId);
        }
      }
    });

    let changed = false;
    const updatedPlaylist = playlistRef.current.map((item) => {
      const maId = item.ma_id as string;
      if (actions[maId] && actions[maId].originalIsLiked !== actions[maId].currentIsLiked) {
        changed = true;
        const isLiked = actions[maId].currentIsLiked;
        const wasLiked = !!item.isLikedByUser;
        const diff = isLiked && !wasLiked ? 1 : !isLiked && wasLiked ? -1 : 0;
        return {
          ...item,
          isLikedByUser: isLiked,
          likeCount: Math.max(0, (item.likeCount || 0) + diff),
        };
      }
      return item;
    });

    if (changed) {
      saveVerticalPlayerSession(updatedPlaylist, useAccountStore.getState().user?.id ?? null);
      playlistRef.current = updatedPlaylist;
    }

    const flushedMaIds = [...toLike, ...toUnlike];
    pendingActionsRef.current = {};

    try {
      await Promise.all([
        toLike.length > 0 ? likeMedias({ variables: { mediaIds: toLike, playlistId } }) : Promise.resolve(),
        toUnlike.length > 0 ? unlikeMedias({ variables: { mediaIds: toUnlike, playlistId } }) : Promise.resolve(),
      ]);
    } finally {
      if (flushedMaIds.length > 0) {
        const res = await refetch();
        // refetch() shape depends on your usePlaylist hook — adjust the path below
        // (e.g. res?.data?.playlist) to match what it actually returns.
        const freshPlaylist: PlaylistItem[] = (res as any)?.data?.playlist ?? (res as any)?.playlist ?? [];

        if (freshPlaylist.length) {
          const freshMap = new Map(freshPlaylist.map((i: any) => [i.ma_id, i]));

          setPlaylist((prev) => {
            const synced = prev.map((item) => {
              const maId = item.ma_id as string;
              if (!flushedMaIds.includes(maId)) { return item; }
              const fresh = freshMap.get(maId);
              if (!fresh) {
                // API hasn't reflected it yet, drop the pending flag so a later refresh
                // is free to trust the API again
                const { __hasPendingLikeAction, ...rest } = item as any;
                return rest;
              }
              return {
                ...item,
                likeCount: fresh.likeCount,
                isLikedByUser: fresh.isLikedByUser,
                __hasPendingLikeAction: false,
              } as PlaylistItem;
            });
            playlistRef.current = synced;
            saveVerticalPlayerSession(synced, useAccountStore.getState().user?.id ?? null);
            return synced;
          });
        }

        // now that the server has confirmed, drop the optimistic override so the UI
        // reads from playlist/currentVideo (i.e. the real API value) again
        setLikeStates((prev) => {
          const next = { ...prev };
          flushedMaIds.forEach((maId) => {
            delete next[maId];
          });
          return next;
        });
      }
    }
  }, [likeMedias, unlikeMedias, playlistId, refetch]);

  const handleLikeToggle = useCallback(() => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    if (!currentVideo) { return; }
    const maId = currentVideo.ma_id as string;
    if (!maId) { return; }

    const newIsLiked = !currentLikeInfo.isLiked;
    const newCount = Math.max(0, currentLikeInfo.count + (newIsLiked ? 1 : -1));

    setLikeStates((prev) => ({
      ...prev,
      [maId]: { isLiked: newIsLiked, count: newCount },
    }));
    persistLikeToPlaylist(maId, newIsLiked, newCount);

    if (!pendingActionsRef.current[maId]) {
      pendingActionsRef.current[maId] = {
        originalIsLiked: !!currentVideo.isLikedByUser,
        currentIsLiked: newIsLiked,
      };
    } else {
      pendingActionsRef.current[maId].currentIsLiked = newIsLiked;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      flushPendingActions();
    }, 1000); // 1 second debounce before syncing with the server
  }, [isLoggedIn, currentVideo, currentLikeInfo, flushPendingActions, navigate, persistLikeToPlaylist]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      const actions = pendingActionsRef.current;
      const toLike: string[] = [];
      const toUnlike: string[] = [];

      Object.entries(actions).forEach(([maId, { originalIsLiked, currentIsLiked }]) => {
        if (originalIsLiked !== currentIsLiked) {
          if (currentIsLiked) {
            toLike.push(maId);
          } else {
            toUnlike.push(maId);
          }
        }
      });

      let changed = false;
      const updatedPlaylist = playlistRef.current.map((item) => {
        const maId = item.ma_id as string;
        if (actions[maId] && actions[maId].originalIsLiked !== actions[maId].currentIsLiked) {
          changed = true;
          const isLiked = actions[maId].currentIsLiked;
          const wasLiked = !!item.isLikedByUser;
          const diff = isLiked && !wasLiked ? 1 : !isLiked && wasLiked ? -1 : 0;
          return {
            ...item,
            isLikedByUser: isLiked,
            likeCount: Math.max(0, (item.likeCount || 0) + diff),
          };
        }
        return item;
      });

      if (changed) {
        saveVerticalPlayerSession(updatedPlaylist, useAccountStore.getState().user?.id ?? null);
        playlistRef.current = updatedPlaylist;
      }

      Promise.all([
        toLike.length > 0 ? likeMedias({ variables: { mediaIds: toLike, playlistId } }) : Promise.resolve(),
        toUnlike.length > 0 ? unlikeMedias({ variables: { mediaIds: toUnlike, playlistId } }) : Promise.resolve(),
      ]).finally(() => {
        if (toLike.length > 0 || toUnlike.length > 0) {
          refetch();
        }
      });
    };
  }, [likeMedias, unlikeMedias, playlistId, refetch]);

  const prevUserIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (prevUserIdRef.current === undefined) {
      prevUserIdRef.current = userId;
      return;
    }
    if (prevUserIdRef.current === userId) { return; }

    const hadPreviousUser = prevUserIdRef.current !== null;
    prevUserIdRef.current = userId;

    setLikeStates({});
    pendingActionsRef.current = {};
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (hadPreviousUser || userId === null) {
      setPlaylist((prev) => prev.map((item) => ({ ...item, isLikedByUser: false })));
    }
  }, [userId]);

  // Sync session like state during in-app navigation only (not on shared-link entry)
  useEffect(() => {
    if (initialMedia) { return; }

    const session = getVerticalPlayerSession();
    if (!session?.playlist?.length) { return; }
    setPlaylist((prev) => {
      if (!prev.length) { return prev; }
      return mergeSessionDataIntoPlaylist(prev, session, userId);
    });
  }, [userId, initialMedia]);

  // useParams does not update when URL changes via replaceState while scrolling between reels.
  // This only needs to run ONCE, to land on the correct initial reel (e.g. a shared/direct link
  // target). sharedTargetIdRef never updates after mount, so if this kept running on every
  // playlist reference change (loadMoreReels, like-count sync, etc.) it would keep yanking the
  // user back to that original target video even after they've since scrolled elsewhere.
  const hasSyncedInitialPositionRef = useRef(false);
  useEffect(() => {
    if (!playlist.length) { return; }
    if (hasSyncedInitialPositionRef.current) { return; }

    const pathMediaId = sharedTargetIdRef.current || getActiveMediaIdFromPath(mediaId);
    if (!pathMediaId) {
      hasSyncedInitialPositionRef.current = true;
      return;
    }

    const index = findReelIndex(playlist, pathMediaId);
    if (index < 0) { return; } // target not in playlist yet (still loading) — try again on next update

    hasSyncedInitialPositionRef.current = true;
    if (index !== currentIndexRef.current) {
      setCurrentIndex(index);
    }
  }, [playlist, mediaId]);

  const prependTargetToPlaylist = useCallback(
    (newPlaylist: PlaylistItem[], targetId: string, resolvedMedia: PlaylistItem) => {
      const withoutDuplicate = newPlaylist.filter(
        (item) =>
          !matchesReelId(item, targetId) &&
          !matchesReelId(item, resolvedMedia.mediaid) &&
          !(resolvedMedia.ma_id && matchesReelId(item, resolvedMedia.ma_id as string)),
      );
      setPlaylist([resolvedMedia, ...withoutDuplicate]);
      setCurrentIndex(0);
      setLikeStates({});
      lastMediaIdRef.current = resolvedMedia.mediaid;
    },
    [],
  );

  useEffect(() => {
    if (playlist.length >= 2) {
      hasHydratedPlaylistRef.current = true;
      return;
    }

    if (location.state?.playlistItems?.length) { return; }
    if (!fetchedPlaylist?.playlist?.length) { return; }

    if (hasHydratedPlaylistRef.current) { return; }

    const newPlaylist = fetchedPlaylist.playlist;
    const targetId = sharedTargetIdRef.current || getReelTargetId(mediaId, initialMedia);
    const index = targetId ? findReelIndex(newPlaylist, targetId) : 0;
    const resolvedMedia = initialMedia || targetMedia;

    if (index < 0 && resolvedMedia) {
      prependTargetToPlaylist(newPlaylist, targetId, resolvedMedia);
      hasHydratedPlaylistRef.current = true;
      return;
    }

    if (index < 0 && shouldLoadTargetMedia && isTargetMediaLoading) {
      return;
    }

    setPlaylist(newPlaylist);
    setCurrentIndex(index >= 0 ? index : 0);
    setLikeStates({});
    if (index >= 0) {
      lastMediaIdRef.current = newPlaylist[index]?.mediaid;
    }
  }, [fetchedPlaylist, mediaId, initialMedia, targetMedia, isTargetMediaLoading, shouldLoadTargetMedia, prependTargetToPlaylist, location.state?.playlistItems]);

  // Reconcile a sessionStorage-seeded initial render (hard refresh / direct load, no router
  // navigation state) with the fresh API fetch once it arrives. This is what actually fixes
  // "refresh doesn't show another user's new like count": the playlist above may already have
  // length >= 2 from sessionStorage, so the hydration effect right above this one will skip
  // (its `playlist.length >= 2` guard fires first) — this effect exists specifically to still
  // pull in fresh likeCount/isLikedByUser in that case, without discarding scroll position,
  // ordering, or any other session-only state already in the playlist.
  const refreshSyncDoneRef = useRef(false);
  useEffect(() => {
    if (refreshSyncDoneRef.current) { return; }
    if (!isSessionOnlyEntryRef.current) { return; }
    if (!fetchedPlaylist?.playlist?.length) { return; }

    refreshSyncDoneRef.current = true;

    const freshMap = new Map(fetchedPlaylist.playlist.map((i: any) => [i.ma_id, i]));

    setPlaylist((prev) => {
      const synced = prev.map((item) => {
        const fresh = freshMap.get(item.ma_id as string);
        if (!fresh) { return item; }
        // don't clobber an action the user just took locally and hasn't been confirmed yet
        if ((item as any).__hasPendingLikeAction) { return item; }
        return { ...item, likeCount: fresh.likeCount, isLikedByUser: fresh.isLikedByUser };
      });
      playlistRef.current = synced;
      saveVerticalPlayerSession(synced, useAccountStore.getState().user?.id ?? null);
      return synced;
    });
  }, [fetchedPlaylist]);

  useEffect(() => {
    if (targetMedia && playlist.length === 0 && !fetchedPlaylist?.playlist?.length) {
      setPlaylist([targetMedia]);
      setHasMore(false);
      setCurrentIndex(0);
      lastMediaIdRef.current = targetMedia.mediaid;
    }
  }, [targetMedia, playlist.length, fetchedPlaylist?.playlist?.length]);

  const loadMoreReels = useCallback(async () => {
    if (!playlistId || loadingMore || !hasMore) { return; }
    try {
      setLoadingMore(true);

      const res = await apiService.getReelsByPlaylistId(playlistId, PAGE_SIZE, offset);

      const newItems = Array.isArray(res?.playlist) ? res.playlist : [];
      if (newItems.length < PAGE_SIZE) {
        setHasMore(false);
      }

      setPlaylist((prev) => {
        const existingIds = new Set(prev.map((i) => i.mediaid));

        const filteredNew = newItems.filter((item: any) => !existingIds.has(item.mediaid));

        const updated = [...prev, ...filteredNew];

        return updated;
      });

      setOffset((prev) => prev + PAGE_SIZE);
    } catch (err) {
      console.error('Failed to load more reels:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [playlistId, offset, loadingMore, hasMore]);

  const handleNext = useCallback(() => {
    if (currentIndex >= playlist.length - 1 || isAnimating) { return; }

    const nextIndex = currentIndex + 1;
    if (nextIndex >= playlist.length - 3) {
      loadMoreReels();
    }

    setCurrentTime(0);
    setDuration(0);

    setDirection('next');
    setIsAnimating(true);

    setTimeout(() => {
      setCurrentIndex(nextIndex);

      requestAnimationFrame(() => {
        setIsAnimating(false);
      });
    }, 180);
  }, [currentIndex, playlist.length, isAnimating, loadMoreReels]);

  const handlePrev = useCallback(() => {
    if (currentIndex <= 0 || isAnimating) { return; }

    setCurrentTime(0);
    setDuration(0);

    setDirection('prev');
    setIsAnimating(true);

    setTimeout(() => {
      setCurrentIndex((prev) => prev - 1);

      requestAnimationFrame(() => {
        setIsAnimating(false);
      });
    }, 180);
  }, [currentIndex, isAnimating]);

  const handleComplete = useCallback(() => {
    playerInstance?.seek(0);
    playerInstance?.play();
  }, [playerInstance]);

  const handleReady = useCallback(
    (player?: JWPlayer) => {
      if (player) {
        setCurrentTime(0);
        setDuration(0);

        setPlayerInstance(player);
        setDuration(player.getDuration() || 0);
        setIsMuted(player.getMute());

        player.on('time', (event) => {
          setCurrentTime(event.position);
          setDuration(event.duration);
        });

        player.on('play', () => setIsPaused(false));
        player.on('pause', () => setIsPaused(true));
        player.on('mute', (event) => setIsMuted(event.mute));
      }
    },
    [setCurrentTime, setDuration],
  );

  const togglePlayPause = useCallback(() => {
    if (!playerInstance) { return; }

    const state = playerInstance.getState();
    if (state === 'playing') {
      playerInstance.pause();
    } else {
      playerInstance.play();
    }
  }, [playerInstance]);

  const toggleMute = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (!playerInstance) { return; }
      const newMuted = !isMuted;
      playerInstance.setMute(newMuted);
      setIsMuted(newMuted);
      if (!newMuted && volume === 0) {
        setVolume(1);
        playerInstance.setVolume(100);
      }
    },
    [playerInstance, isMuted, volume],
  );

  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = Number.parseFloat(e.target.value);
      setVolume(val);
      if (playerInstance) {
        playerInstance.setVolume(val * 100);
        if (val > 0 && isMuted) {
          playerInstance.setMute(false);
          setIsMuted(false);
        } else if (val === 0 && !isMuted) {
          playerInstance.setMute(true);
          setIsMuted(true);
        }
      }
    },
    [playerInstance, isMuted],
  );

  const handleSeek = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (!playerInstance || duration <= 0) { return; }

      const rect = e.currentTarget.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      playerInstance.seek(pos * duration);
    },
    [playerInstance, duration],
  );

  const touchStartRef = useRef<number | null>(null);
  const scrollLockRef = useRef<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) { return; }

    const handleTouchStart = (e: TouchEvent) => {
      touchStartRef.current = e.touches[0].clientY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartRef.current === null || modalView) { return; }
      const touchMove = e.touches[0].clientY;
      const diff = touchStartRef.current - touchMove;
      if (Math.abs(diff) > 5) {
        if (e.cancelable) { e.preventDefault(); }
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (touchStartRef.current === null || modalView) { return; }
      const touchEnd = e.changedTouches[0].clientY;
      const diff = touchStartRef.current - touchEnd;
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          handleNext();
        } else {
          handlePrev();
        }
      }
      touchStartRef.current = null;
    };

    wrapper.addEventListener('touchstart', handleTouchStart, { passive: true });
    wrapper.addEventListener('touchmove', handleTouchMove, { passive: false });
    wrapper.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      wrapper.removeEventListener('touchstart', handleTouchStart);
      wrapper.removeEventListener('touchmove', handleTouchMove);
      wrapper.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleNext, handlePrev, modalView]);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (scrollLockRef.current || modalView) { return; }
      if (Math.abs(e.deltaY) > 30) {
        if (e.deltaY > 0) {
          handleNext();
        } else {
          handlePrev();
        }
        scrollLockRef.current = true;
        setTimeout(() => {
          scrollLockRef.current = false;
        }, 800);
      }
    };
    window.addEventListener('wheel', handleWheel, { passive: true });
    return () => window.removeEventListener('wheel', handleWheel);
  }, [handleNext, handlePrev, modalView]);

  useEffect(() => {
    if (playlist[currentIndex] && playlist[currentIndex].mediaid !== lastMediaIdRef.current) {
      const currentItem = playlist[currentIndex];
      const newPath = window.location.pathname.replace(lastMediaIdRef.current || '', currentItem.mediaid);
      window.history.replaceState(null, '', `${newPath}${window.location.search}`);
      lastMediaIdRef.current = currentItem.mediaid;
    }
  }, [currentIndex, playlist]);

  useEffect(() => {
    if (modalView && playerInstance && !isPaused) {
      playerInstance.pause();
    }
  }, [modalView, playerInstance, isPaused]);
  useEffect(() => {
    setCurrentTime(0);
    setDuration(0);
  }, [currentIndex]);

  useEffect(() => {
    if (
      playlistId &&
      hasMore &&
      playlist.length > 0 &&
      currentIndex >= playlist.length - 1
    ) {
      loadMoreReels();
    }
  }, [currentIndex, playlist.length, hasMore, playlistId, loadMoreReels]);

  const handleShowMore = () => {
    const currentVideo = playlist[currentIndex];
    if (currentVideo) {
      const newState = { item: currentVideo, primaryMetadata: metadataString };
      sessionStorage.setItem('seeMoreDetails', JSON.stringify(newState));

      navigate(modalURLFromLocation(location, 'show-more-details'), {
        state: newState,
      });
    }
  };

  if (!hasNavigationPlaylist && isLoading && !initialMedia && playlist.length === 0) {
    return <LoadingOverlay />;
  }

  const isInitialLoading = isLoading && !playlist.length && !initialMedia;

  if (isInitialLoading) {
    return <LoadingOverlay />;
  }

  const isHydrating = (isLoading || isTargetMediaLoading) && !playlist.length && !initialMedia;
  if (isHydrating) {
    return <LoadingOverlay />;
  }

  if (!playlist || playlist.length === 0) {
    return <div className={styles.empty}>No videos available.</div>;
  }
  const progressPercent = currentTime > 0 && duration > 0 ? (currentTime / duration) * 100 : 0;

  const creatorAvatarSrc = currentVideo?.creatorAvatarUrl ? `${env.APP_BASE_URL}${currentVideo.creatorAvatarUrl}` : DefaultAvatar;

  const isPlayerReady = !!currentVideo?.mediaid && Array.isArray(currentVideo?.sources) && currentVideo.sources.length > 0;
  return (
    <div
      className={classNames(
        styles.wrapper,
        { [styles.disabled]: !!modalView },
        isAnimating && {
          [styles.slideNext]: direction === 'next',
          [styles.slidePrev]: direction === 'prev',
        },
      )}
    >
      {' '}
      <div className={styles.header}>
        <IconButton aria-label="Go Back" onClick={() => handleBackNavigate(navigate)} className={styles.backBtn}>
          <Icon icon={ArrowLeft} />
        </IconButton>
        <div className={styles.metaInfo}>
          <h1 className={styles.videoTitle}>{currentVideo?.title}</h1>
          <CollapsibleText
            item={currentVideo}
            text={currentVideo?.description}
            className={styles.videoDescription}
            maxWords={12}
            showMoreClickHandler={handleShowMore}
          />
          {currentVideo?.creatorName && (
            <div className={styles.authorContainer}>
              <ProfileCircle src={creatorAvatarSrc} alt={currentVideo.creatorName} className={styles.authorAvatar} />

              <span className={styles.authorName}>{currentVideo.creatorName}</span>
            </div>
          )}
          {!!currentVideo?.category && (
            <span className={classNames(styles.categoryBadge, styles.mobileCategory)}>{currentVideo?.category || currentVideo?.genre}</span>
          )}
          {/* <div className={styles.authorBadge}>
              <img src={(currentVideo?.authorImage as string) || 'https://ui-avatars.com/api/?name=User'} alt="Author" className={styles.avatar} />
              <span className={styles.authorName}>{(currentVideo?.author as string) || 'Hudson Joe'}</span>
            </div> */}
        </div>
      </div>
      <div className={styles.centerPanel}>
        <div className={styles.verticalContainer}>
          <div className={styles.playerWrapper} style={{ pointerEvents: 'none' }}>
            {isPlayerReady ? (
              <Player
                key={currentVideo.mediaid}
                item={currentVideo}
                onComplete={handleComplete}
                onReady={handleReady}
                autostart={true}
                mute={false}
                ReConnecting={t('reconnecting', { defaultValue: 'Reconnecting...' })}
                disableWatchHistory={true}
              />
            ) : (
              <LoadingOverlay />
            )}
          </div>
          <div className={styles.gestureOverlay} onClick={togglePlayPause} />

          <div className={styles.topLeftControls}>
            {!!currentVideo?.category && <span className={styles.categoryBadge}>{currentVideo?.category || currentVideo?.genre}</span>}
          </div>

          <div
            className={styles.volumeControlWrapper}
            onMouseEnter={() => setShowVolumeBar(true)}
            onMouseLeave={() => setShowVolumeBar(false)}
            onClick={(e) => e.stopPropagation()}
          >
            {showVolumeBar && (
              <div className={styles.volumeBarContainer} onClick={(e) => e.stopPropagation()}>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className={styles.volumeSlider}
                  style={{ '--percent': `${(isMuted ? 0 : volume) * 100}%` } as React.CSSProperties}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
            <IconButton className={styles.muteBtn} onClick={toggleMute}>
              <Icon icon={isMuted || volume === 0 ? AudioOff : AudioOn} />
            </IconButton>
          </div>

          {isPaused && (
            <div className={styles.playPauseOverlay}>
              <Icon icon={PlayIcon} />
            </div>
          )}

          <div className={styles.progressContainer} onClick={handleSeek}>
            <div className={styles.progressBarRail}>
              <div className={styles.progressBar} style={{ width: `${progressPercent}%` }} />
            </div>
          </div>
        </div>
        {currentVideo && (
          <div className={styles.sideActions}>
            {currentVideo.ma_id && (
              <div className={styles.actionItem}>
                <button
                  className={classNames(styles.actionBtn, { [styles.liked]: currentLikeInfo.isLiked })}
                  onClick={handleLikeToggle}
                  aria-label={currentLikeInfo.isLiked ? 'Unlike Video' : 'Like Video'}
                >
                  <LikeIcon isLiked={currentLikeInfo.isLiked} fillColor="#ff9f1c" width={24} height={24} />
                </button>
                <span className={styles.actionLabel}>{currentLikeInfo.count}</span>
              </div>
            )}

            {isShareEnabled && (
              <div className={styles.shareBtnWrapper}>
                <ShareButton
                  title={currentVideo.title || ''}
                  description={currentVideo.description || ''}
                  url={
                    currentVideo.mediaid
                      ? `${window.location.origin}${shareMediaURL(currentVideo.mediaid, playlistId)}`
                      : window.location.href
                  }
                  mediaId={currentVideo.mediaid}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <div className={styles.rightPanel}>
        <div className={styles.navigationArrows}>
          <IconButton
            className={classNames(styles.backBtn, styles.arrowBtn, { [styles.disabled]: currentIndex === 0 })}
            onClick={handlePrev}
            disabled={currentIndex === 0}
            aria-label="Previous Video"
          >
            <Icon icon={ArrowUp} />
          </IconButton>
          <IconButton
            className={classNames(styles.backBtn, styles.arrowBtn, {
              [styles.disabled]: currentIndex === playlist.length - 1 && !hasMore,
            })}
            onClick={handleNext}
            disabled={currentIndex === playlist.length - 1 && !hasMore}
            aria-label="Next Video"
          >
            <Icon icon={ArrowDown} />
          </IconButton>
        </div>
      </div>
    </div>
  );
};

export default VerticalPlayer;
